import { capitalize } from '#utils/string/index.ts';
import {
  getMonthOffset,
  hasDateTimeComponents,
  isSameDay,
  omitUndefinedOptions,
} from './date.helpers.ts';
import type {
  TCalendarDateTimeOptions,
  TDateInput,
  TFormatDateOptions,
} from './date.types.ts';

export const DEFAULT_LOCALE = 'en-US';

const defaultDateFormat: Intl.DateTimeFormatOptions = { dateStyle: 'medium' };

const defaultDateTimeFormat: Intl.DateTimeFormatOptions = {
  ...defaultDateFormat,
  timeStyle: 'short',
};

const timeOfDayPattern = /[T\s]\d{2}:\d{2}/;

const formatters = new Map<string, Intl.DateTimeFormat>();

const getFormatter = (
  locale: string,
  format: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat => {
  const key = `${locale}|${JSON.stringify(format)}`;
  const cached = formatters.get(key);

  if (cached) {
    return cached;
  }

  const formatter = new Intl.DateTimeFormat(locale, format);

  formatters.set(key, formatter);

  return formatter;
};

const formatWithDefaults = (
  date: TDateInput,
  { locale = DEFAULT_LOCALE, ...options }: TFormatDateOptions,
  defaults: Intl.DateTimeFormatOptions,
): string => {
  const definedOptions = omitUndefinedOptions(options);
  const format = hasDateTimeComponents(definedOptions)
    ? definedOptions
    : { ...defaults, ...definedOptions };

  const formatter = getFormatter(locale, format);
  const value = new Date(date);

  return formatter.format(value);
};

export const hasTimeOfDay = (date: TDateInput): boolean =>
  typeof date !== 'string' || timeOfDayPattern.test(date);

export const formatDate = (
  date: TDateInput,
  options: TFormatDateOptions = {},
): string => formatWithDefaults(date, options, defaultDateFormat);

export const formatDateTime = (
  date: TDateInput,
  options: TFormatDateOptions = {},
): string => formatWithDefaults(date, options, defaultDateTimeFormat);

export const formatDateOrDateTime = (
  date: TDateInput,
  options: TFormatDateOptions = {},
): string =>
  hasTimeOfDay(date)
    ? formatDateTime(date, options)
    : formatDate(date, options);

export const getEndOfDay = (date: TDateInput): Date => {
  const endOfDay = new Date(date);

  endOfDay.setHours(23, 59, 59, 999);

  return endOfDay;
};

export const getStartOfMonth = (date: TDateInput): Date => {
  const value = new Date(date);

  return new Date(value.getFullYear(), value.getMonth(), 1);
};

export const formatCalendarDateTime = (
  date: TDateInput,
  { locale = DEFAULT_LOCALE, now = new Date() }: TCalendarDateTimeOptions = {},
): string => {
  if (!isSameDay(date, now)) {
    return formatDateTime(date, {
      locale,
      dateStyle: 'long',
      timeStyle: 'short',
    });
  }

  const today = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' }).format(
    0,
    'day',
  );

  const time = formatDateTime(date, { locale, timeStyle: 'short' });

  return `${capitalize(today, locale)} ${time}`;
};

export const formatCalendarMonth = (
  month: TDateInput,
  { locale = DEFAULT_LOCALE, now = new Date() }: TCalendarDateTimeOptions = {},
): string => {
  const offset = getMonthOffset(month, now);

  if (offset !== 0 && offset !== 1) {
    return formatDate(month, { locale, month: 'long', year: 'numeric' });
  }

  const relativeMonth = new Intl.RelativeTimeFormat(locale, {
    numeric: 'auto',
  }).format(-offset, 'month');

  return capitalize(relativeMonth, locale);
};
